if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      constructor() {
        super();
        this.input = this.querySelector('.quantity__input');
        this.currentVariant = this.querySelector('.product-variant-id');
        this.variantSelects = this.querySelector('variant-radios');
        this.submitButton = this.querySelector('.product-form__submit--primary');
        this.stickySubmitButton = this.querySelector('.product-form__submit--sticky');
        this.scrollToSectionCTA = this.querySelector('[data-scroll-to-section]');
      }

      cartUpdateUnsubscriber = undefined;
      variantChangeUnsubscriber = undefined;

      connectedCallback() {
        this.initStickySubmit();

        if (this.scrollToSectionCTA) {
          this.setupScrollToSection();
        }

        if (!this.input) return;
        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;
        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const sectionId = this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section;
          if (event.data.sectionId !== sectionId) return;
          this.updateQuantityRules(event.data.sectionId, event.data.html);
          this.setQuantityBoundries();
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
        if (this.variantChangeUnsubscriber) {
          this.variantChangeUnsubscriber();
        }
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.input.dataset.cartQuantity ? parseInt(this.input.dataset.cartQuantity) : 0,
          min: this.input.dataset.min ? parseInt(this.input.dataset.min) : 1,
          max: this.input.dataset.max ? parseInt(this.input.dataset.max) : null,
          step: this.input.step ? parseInt(this.input.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.input.min = min;
        this.input.max = max;
        this.input.value = min;
        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        if (!this.currentVariant || !this.currentVariant.value) return;
        this.querySelector('.quantity__rules-cart .loading-overlay').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${this.currentVariant.value}&section_id=${this.dataset.section}`)
          .then((response) => {
            return response.text();
          })
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
            this.setQuantityBoundries();
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.querySelector('.quantity__rules-cart .loading-overlay').classList.add('hidden');
          });
      }

      updateQuantityRules(sectionId, html) {
        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) current.setAttribute(attribute, valueUpdated);
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      initStickySubmit() {
        if (!this.stickySubmitButton) return;

        const footer = document.querySelector('footer.footer');
        let isPrimaryBtnAboveViewport = false;
        let isFooterVisible = false;
        const boundUpdateStickyCtaVisibility = updateStickyCtaVisibility.bind(this);

        // Show sticky CTA when primary button is above viewport
        const btnObserver = new IntersectionObserver(
          ([entry]) => {
            isPrimaryBtnAboveViewport = entry.boundingClientRect.bottom < 0;
            boundUpdateStickyCtaVisibility();
          },
          {
            root: null,
            threshold: 0,
          }
        );

        // Hide sticky CTA when footer is in viewport
        const footerObserver = new IntersectionObserver(
          ([entry]) => {
            isFooterVisible = entry.isIntersecting;
            boundUpdateStickyCtaVisibility();
          },
          {
            root: null,
            rootMargin: '0px 0px -100px 0px',
            threshold: 0,
          }
        );

        btnObserver.observe(this.submitButton);
        footerObserver.observe(footer);

        function updateStickyCtaVisibility() {
          this.stickySubmitButton.classList.toggle('visible', isPrimaryBtnAboveViewport && !isFooterVisible);
        }
      }

      setupScrollToSection() {
        const sectionToScrollToUrl = this.scrollToSectionCTA.dataset.scrollToSection;

        if (!sectionToScrollToUrl.includes('#')) {
          return;
        }

        const sectionToScrollTo = document.querySelector(sectionToScrollToUrl);
        const headerGroupSections = document.querySelectorAll('.shopify-section-group-header-group');

        if (sectionToScrollTo) {
          this.scrollToSectionCTA.addEventListener('click', (e) => {
            e.preventDefault();

            const elementOffsetTop = sectionToScrollTo.offsetTop;
            const totalHeaderOffset = Array.from(headerGroupSections).reduce((total, el) => {
              return total + el.offsetHeight;
            }, 0);
            const scrollTop = elementOffsetTop - totalHeaderOffset;

            window.scrollTo({
              top: scrollTop,
              behavior: 'smooth',
            });
          });
        } else {
          this.scrollToSectionCTA.remove();
        }
      }
    }
  );
}

function onVariantChange({ target: optionValueElement }) {
  if (!optionValueElement.dataset.variantId) {
    return false;
  }

  const productElement = this.querySelector('.product');
  const sectionId = this.dataset.sectionId;
  const oldProductUrl = productElement.dataset.productUrl;
  const newProductUrl = optionValueElement.dataset.productUrl;
  const newProductTitle = optionValueElement.dataset.productTitle;
  const newVariantId = optionValueElement.dataset.variantId;

  // Safari iOS restores the checked swatch and fires `change` on first paint.
  // A no-op fetch still dispatches section:load and used to tear down Amazon's
  // first-load Buy with Prime widget.
  const currentVariantId = this.querySelector('input.product-variant-id')?.value;
  const sameProduct = !newProductUrl || oldProductUrl === newProductUrl;
  if (sameProduct && currentVariantId && String(currentVariantId) === String(newVariantId)) {
    return false;
  }
  const stickyAtcButton = document.querySelector('.product-form__submit--sticky');
  const isGiftCard = Boolean(this.querySelector('.gift-card-denomination, .option-denominations'));

  // Gift Card: keep ATC form in sync with the selected denomination
  if (isGiftCard) {
    updateGiftCardVariantId(this, newVariantId);
  }

  let params = `section_id=${sectionId}&`;
  if (newVariantId) {
    // If the option value has a variant selection, call the section rendering api for that variant
    params += `variant=${newVariantId}`;
  } else {
    // If the option value selection has no variant, call the section rendering api with the selected option value IDs instead
    const selectedOptionValues = Array.from(this.querySelectorAll('.fieldset input:checked'))
      .map((element) => element.dataset.optionValueId)
      .join(',');
    params += `option_values=${selectedOptionValues}`;
  }

  // Call the section rendering api. variant.url already carries a ?variant= query
  // string, so strip it before appending ours — otherwise the URL has two '?' and
  // section_id never parses, which silently returns the whole page instead.
  fetch(`${newProductUrl.split('?')[0]}?${params}`)
    .then((response) => response.text())
    .then((responseText) => {
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      if (newProductUrl && oldProductUrl !== newProductUrl) {
        // If the product changed, replace the old product section with the new product section
        const newProductElement = html.querySelector('.product');

        // A failed section render leaves us with nothing to insert. Fall back to a
        // normal navigation so the shopper still lands on the variant they picked.
        if (!newProductElement) {
          window.location.href = newProductUrl;
          return;
        }

        productElement.parentNode.insertBefore(newProductElement, productElement);
        productElement.remove();

        // Focus the input for the last clicked option value
        this.querySelector(`#${optionValueElement.id}`).focus();

        // Update the URL
        updateUrl(newProductUrl, newProductTitle);
        renderPDPSections(newProductUrl);

        const event = new Event('section:load');
        document.dispatchEvent(event);
        document.dispatchEvent(new Event('bwp:product-swap'));

        // Update anything else that depends on product, such as URL and recommended products
        return;
      }

      this.querySelector('.optionValueSelectors').innerHTML = html.querySelector('.optionValueSelectors').innerHTML;
      this.querySelector(`#${optionValueElement.id}`).focus();

      const priceEl = this.querySelector('.price');
      if (newVariantId) {
        priceEl.innerHTML = html.querySelector('.price').innerHTML;
        // Update any additional states here
      } else {
        priceEl.innerHTML = '';
        // Set additional unavailable states here
      }

      if (isGiftCard) {
        updateGiftCardVariantId(this, newVariantId);
        updateGiftCardLegend(this, html);
      }

      stickyAtcButton?.classList.remove('visible');

      // Update the URL
      updateUrl(newProductUrl, newProductTitle);
      const event = new Event('section:load');
      document.dispatchEvent(event);
    })
    .catch((error) => {
      // Without this the failure surfaces only as an unhandled rejection, and every
      // step below the throw — including the section:load dispatch — is skipped.
      console.error('Variant change failed:', error);
      if (newProductUrl) window.location.href = newProductUrl;
    });
}

function updateGiftCardVariantId(root, variantId) {
  if (!variantId) return;

  root.querySelectorAll('input[name="id"]').forEach((input) => {
    input.value = variantId;
  });
}

function updateGiftCardLegend(root, html) {
  const currentTitle = root.querySelector('.variant__selected-title');
  const newTitle = html.querySelector('.variant__selected-title');
  if (currentTitle && newTitle) {
    currentTitle.textContent = newTitle.textContent;
  }

  const currentSku = root.querySelector('.variant__selected-sku');
  const newSku = html.querySelector('.variant__selected-sku');
  if (currentSku && newSku && currentSku.tagName === 'SPAN') {
    currentSku.textContent = newSku.textContent;
  }
}

function updateUrl(newUrl, newTitle) {
  if (newUrl) {
    history.pushState(null, newTitle || '', newUrl);
    if (newTitle) {
      document.title = newTitle;
    }
  }
}

function renderPDPSections(newProductUrl) {
  const sectionIds = [
    'section-product-breadcrumbs',
    'section-band-upsells',
    'section-product-features',
    'section-product-specifications',
    'section-product-full-width',
    'section-product-full-width_video',
    'section-product-fifty_fifty',
    'section-product-fifty_fifty-2',
  ];

  sectionIds.forEach((sectionId) => {
    fetch(`${newProductUrl}?section_id=${sectionId}`)
      .then((response) => response.text())
      .then((html) => {
        const parser = new DOMParser();
        const parsedDocument = parser.parseFromString(html, 'text/html');
        const sectionContent = parsedDocument.querySelector(`[data-section-id="${sectionId}"]`);

        if (sectionContent && document.getElementById(sectionId)) {
          document.getElementById(sectionId).innerHTML = sectionContent.innerHTML;
          makePdpCopyFocusable();
        }
      })
      .catch((error) => {
        console.error(`Failed to load section ${sectionId}: `, error);
      });
  });
}

const productSection = document.getElementById('productSection');

if (productSection) {
  productSection.addEventListener('change', onVariantChange);
  productSection.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter') return;
    const radio = event.target;
    if (!(radio instanceof HTMLInputElement) || radio.type !== 'radio') return;
    event.preventDefault();
    if (radio.checked) return;
    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
  });
}

const PDP_COPY_SELECTORS = [
  '.product__title h1',
  '.product__info-container .price',
  '.product__text',
  '.product__richtext',
  '.product__inventory',
  '.product__sku',
  '.product__description-content',
  '.product__description-line-item',
  '.badge--pdp',
  '.product-form__option-info .form__label',
  '.product-form__option-info legend',
  '.custom-card__title',
  '.custom-card__item-title',
  '.custom-card__item-copy',
  '.custom-card__description',
  '.band-upsells__heading',
  '.accordion__content',
  '.icon-with-text__item',
].join(',');

function makePdpCopyFocusable() {
  document.querySelectorAll(PDP_COPY_SELECTORS).forEach((el) => {
    if (!el.textContent || !el.textContent.trim()) return;
    if (el.closest('a, button, summary')) return;
    if (el.closest('details') && !el.closest('details[open]')) return;
    if (el.querySelector('a[href], button, input, select, textarea')) return;
    el.tabIndex = 0;
  });
}

makePdpCopyFocusable();
document.addEventListener('section:load', makePdpCopyFocusable);
document.addEventListener('toggle', (event) => {
  if (event.target?.matches?.('details')) makePdpCopyFocusable();
}, true);
document.addEventListener('click', (event) => {
  if (event.target.closest('.custom-card__title a')) {
    requestAnimationFrame(makePdpCopyFocusable);
  }
});
